/**
Copyright (c) 2023, 2024, 2025 Amica Corp.
All rights reserved.

This source code, resulting binary forms, and associated documentation
(the "Software") may only be used, modified, or distributed, with or
without modification, under the terms of a written license agreement
with Amica Corp. (the "License"). All rights to manufacture, use,
redistribute, or sell anything described herein, in whole or in part,
are granted only as explicitly permitted by such License. The receipt
or possession of the Software does not convey any of these rights.

This Software is provided "AS IS," WITHOUT WARRANTY OF ANY KIND, express
or implied, including but not limited to the implied warranties of
MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.

For additional terms, see https://amica.cloud/software-copyright/
 */

import { createSelector } from '@reduxjs/toolkit';
import { SLICE } from 'modules/dashboard/constants';
import { initialState } from './slice';
/**
 * Getting entire rate calendar state into the component
 */
const selectDomain = (state) => state[SLICE.RATE_CALENDAR] || initialState;
/**
 * Getting the loader state into the component
 */
export const selectLoader = createSelector(
  [selectDomain],
  (rateCalenderState) => rateCalenderState.loading
);
/**
 * Getting the event details state into the component
 */
export const selectEvents = createSelector(
  [selectDomain],
  (rateCalenderState) => rateCalenderState.eventDetails
);
/**
 * Getting the forecast constraints details state into the component
 */
export const selectForecastDetails = createSelector(
  [selectDomain],
  (rateCalenderState) => rateCalenderState.forecastDetails
);
/**
 * Getting the forecast rate details state into the component
 */
export const selectRateDetails = createSelector(
  [selectDomain],
  (rateCalenderState) => rateCalenderState.rateDetails
);
/**
 * Getting the forecast rate details grouped by date state into the component
 */
export const selectGroupByRatesDetails = createSelector(
  [selectDomain],
  (rateCalenderState) => rateCalenderState.groupRatesDetails
);
/**
 * Getting the forecast constraints details grouped by date state into the component
 */
export const selectGroupByForecastDetails = createSelector(
  [selectDomain],
  (rateCalenderState) => rateCalenderState.groupForecastDetails
);
/**
 * Getting the events details grouped by date state into the component
 */
export const selectGroupByEventsDetails = createSelector(
  [selectDomain],
  (rateCalenderState) => rateCalenderState.groupEventDetails
);
/**
 * Getting the hotel details state into the component
 */
export const selectHotelDetails = createSelector(
  [selectDomain],
  (rateCalenderState) => rateCalenderState.hotelDetails
);
/**
 * Getting the room consumption details state into the component
 */
export const selectRoomConsumptionDetails = createSelector(
  [selectDomain],
  (rateCalenderState) => rateCalenderState.roomConsumptionDetails
);
/**
 * Getting the room consumption details grouped by date state into the component
 */
export const selectGroupRoomConsumptionDetails = createSelector(
  [selectDomain],
  (rateCalenderState) => rateCalenderState.groupRoomConsumptionDetails
);
/**
 * Getting the major event categories state into the component
 */
export const selectMajorEventCategories = createSelector(
  [selectDomain],
  (rateCalenderState) => rateCalenderState.majorEventCategories
);
/**
 * Getting the minor event categories state into the component
 */
export const selectMinorEventCategories = createSelector(
  [selectDomain],
  (rateCalenderState) => rateCalenderState.minorEventCategories
);
/**
 * Getting the previous year room consumption details state into the component
 */
export const selectPreviousYearRoomConsumptionDetails = createSelector(
  [selectDomain],
  (rateCalenderState) => rateCalenderState.previousYearRoomConsumptionDetails
);
/**
 * Getting the previous year room consumption details grouped by date state into the component
 */
export const selectPreviousYearGroupRoomConsumptionDetails = createSelector(
  [selectDomain],
  (rateCalenderState) => rateCalenderState.groupPreviousYearRoomConsumptionDetails
);
/**
 * Getting the previous year forecast rate details state into the component
 */
export const selectPreviousYearRateDetails = createSelector(
  [selectDomain],
  (rateCalenderState) => rateCalenderState.previousYearRateDetails
);
/**
 * Getting the previous year forecast rate details grouped by date state into the component
 */
export const selectPreviousYearGroupByRatesDetails = createSelector(
  [selectDomain],
  (rateCalenderState) => rateCalenderState.previousYearGroupRatesDetails
);
/**
 * Getting the previous year forecast constraints details state into the component
 */
export const selectPreviousYearForecastDetails = createSelector(
  [selectDomain],
  (rateCalenderState) => rateCalenderState.previousYearForecastDetails
);
/**
 * Getting the previous year forecast constraints details grouped by date state into the component
 */
export const selectGroupPreviousYearForecastDetails = createSelector(
  [selectDomain],
  (rateCalenderState) => rateCalenderState.groupPreviousYearForecastDetails
);
/**
 * Getting the rate calendar selected date into the component
 */
export const selectDateSelected = createSelector([selectDomain], (rateCalenderState) =>
  rateCalenderState.selectedDate ? rateCalenderState.selectedDate : ''
);
/**
 * Getting the rate calendar previous year date into the component
 */
export const selectPreviousYearDate = createSelector(
  [selectDomain],
  (rateCalenderState) => rateCalenderState.previousYearDate
);
/**
 * Getting the rate calendar metrics for selected date into the component
 */
export const selectMetricsRows = createSelector(
  [selectDomain],
  (rateCalenderState) => rateCalenderState.metricsRows
);
/**
 * Getting the rate calendar event list into the component
 */
export const selectCalendarEventList = createSelector(
  [selectDomain],
  (rateCalenderState) => rateCalenderState.calendarEventList
);
